function capitalizeToken(token) {
  if (!token) return '';
  const lower = token.toLowerCase().replace(/\u2019/g, "'");
  return lower.replace(/(^|['-])([a-z])/g, (match, sep, letter, offset, str) => {
    const afterApostrophe = sep === "'";
    if (afterApostrophe && letter === 's' && offset + 2 === str.length) {
      return sep + letter;
    }
    return sep + letter.toUpperCase();
  });
}

/**
 * Title-case street or city text, e.g. "123 main street" → "123 Main Street".
 * Capitalizes the first letter and any letter after an apostrophe or hyphen.
 * @param {string | null | undefined} value
 * @returns {string}
 */
export function toAddressTitleCase(value) {
  if (value == null || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  return trimmed
    .split(/\s+/)
    .map((word) => capitalizeToken(word))
    .join(' ');
}

/**
 * Normalize business address fields for storage (title-case lines/city, uppercase state).
 * Preserves optional fields such as ShowAddressToPublic.
 * @param {object | null | undefined} address
 * @returns {object}
 */
export function normalizeBusinessAddress(address) {
  if (!address || typeof address !== 'object') return {};

  return {
    ...address,
    AddressLine1: toAddressTitleCase(address.AddressLine1),
    AddressLine2: toAddressTitleCase(address.AddressLine2),
    City: toAddressTitleCase(address.City),
    State:
      typeof address.State === 'string'
        ? address.State.trim().toUpperCase()
        : address.State,
    ZipCode:
      typeof address.ZipCode === 'string'
        ? address.ZipCode.trim()
        : address.ZipCode,
  };
}

/** @param {string} fieldName */
export function isBusinessAddressTextField(fieldName) {
  return fieldName === 'AddressLine1' || fieldName === 'AddressLine2' || fieldName === 'City';
}

/**
 * Normalize a single address field value on blur or before submit.
 * @param {string} fieldName
 * @param {string} value
 * @returns {string}
 */
export function normalizeBusinessAddressFieldValue(fieldName, value) {
  if (value == null || typeof value !== 'string') return value;

  if (fieldName === 'State') {
    return value.trim().toUpperCase();
  }
  if (fieldName === 'ZipCode') {
    return value.trim();
  }
  if (isBusinessAddressTextField(fieldName)) {
    return toAddressTitleCase(value);
  }
  return value;
}

/** @param {string | null | undefined} value */
export function normalizeBusinessEmail(value) {
  if (value == null || typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

/** @param {string | null | undefined} value */
export function normalizeBusinessWebsiteUrl(value) {
  if (value == null || typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

/**
 * Normalize a business form field on blur or before submit.
 * Accepts API field names (BusinessEmail) and provider-signup keys (businessEmail).
 * @param {string} fieldName
 * @param {string} value
 * @returns {string}
 */
export function normalizeBusinessFormFieldValue(fieldName, value) {
  if (fieldName === 'BusinessEmail' || fieldName === 'businessEmail') {
    return normalizeBusinessEmail(value);
  }
  if (fieldName === 'BusinessWebsiteUrl' || fieldName === 'businessWebsite') {
    return normalizeBusinessWebsiteUrl(value);
  }
  return normalizeBusinessAddressFieldValue(fieldName, value);
}
